<template>
  <div class="segment-image-panel" v-if="shouldDisplayImage">
    <div class="image-display" @click="openImageUrl(currentImage.url)">
      <div class="image-container">
        <img
          :src="currentImage.image || currentImage.thumbnail"
          :alt="currentImage.title"
          @error="handleImageError"
        />
        <!-- Add Live Transcript component -->
        <live-transcript
          v-if="isPlaying"
          :voice-speed="voiceSpeed"
          :text="activeSegmentContent"
          :speaker="activeSegmentSpeaker"
          :speaker-image="speakerImage"
          :visible="isPlaying"
        />
      </div>
      <div class="image-footer">
        <div class="image-title">{{ currentImage.title }}</div>
        <div class="image-source">
          Source: <a :href="currentImage.url" target="_blank" rel="noopener noreferrer">{{ formatUrl(currentImage.url) }}</a>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State Placeholder (when no content is available) -->
  <div
    v-else-if="!showDocumentsPanel"
    class="empty-state-placeholder"
  >
    <div class="placeholder-image-container">
      <div class="image-container">
        <img
          :src="randomPlaceholderImage"
          alt="Unstruct.ai AI Characters"
          class="placeholder-image"
        />
        <!-- Add Live Transcript component to empty state -->
        <live-transcript
          v-if="isPlaying"
          :voice-speed="voiceSpeed"
          :text="activeSegmentContent"
          :speaker="activeSegmentSpeaker"
          :speaker-image="speakerImage"
          :visible="isPlaying"
        />
      </div>
      <p class="placeholder-text">No document mapped to this segment</p>
    </div>
  </div>
</template>

<script>
import LiveTranscript from './LiveTranscript.vue';

export default {
  name: 'SegmentImagePanel',
  components: {
    LiveTranscript
  },
  props: {
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuofuobi.jpeg'
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    activeSegment: {
      type: Number,
      default: 0
    },
    totalSegments: {
      type: Number,
      default: 0
    },
    showCitationPanel: {
      type: Boolean,
      default: false
    },
    showDocumentsPanel: {
      type: Boolean,
      default: false
    },
    // New props for live transcript
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      previousImageState: false,
      isSegmentImageShowing: false,
      failedImageUrls: new Set(),
      placeholderImages: [
        "/static/Gemini_Generated_Image_afdde4afdde4afdd.jpeg",
        "/static/Gemini_Generated_Image_arxot0arxot0arxo.jpeg",
        "/static/Gemini_Generated_Image_jmfhwfjmfhwfjmfh.jpeg",
        "/static/Gemini_Generated_Image_ttj4r8ttj4r8ttj4.jpeg",
        "/static/Gemini_Generated_Image_28fjog28fjog28fj.jpeg",
        "/static/Gemini_Generated_Image_z58uvpz58uvpz58u.jpeg",
        "/static/Gemini_Generated_Image_2p68i32p68i32p68.jpeg",
        "/static/Gemini_Generated_Image_lmzdtllmzdtllmzd.jpeg",
        "/static/Gemini_Generated_Image_nvx3l3nvx3l3nvx3.jpeg",
        "/static/Gemini_Generated_Image_hrlvv0hrlvv0hrlv.jpeg",
        "/static/Gemini_Generated_Image_wuxx6lwuxx6lwuxx.jpeg",
        "/static/Gemini_Generated_Image_8a43t08a43t08a43.jpeg",
        "/static/Gemini_Generated_Image_cq7nnqcq7nnqcq7n.jpeg",
        "/static/Gemini_Generated_Image_sekakwsekakwseka.jpeg",
        "/static/Gemini_Generated_Image_zcz3ndzcz3ndzcz3.jpeg",
      ],
      // Initialize with a random placeholder image
      currentPlaceholderImageIndex: Math.floor(Math.random() * 2)
    };
  },
  watch: {
    // Watch for changes in the image display state
    shouldDisplayImage(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Emit event when the visibility changes
        this.$emit('image-visibility-changed', newValue, this.currentImage);
        this.isSegmentImageShowing = newValue;
      }
      this.previousImageState = newValue;
    },

    // Watch for changes in active segment
    activeSegment() {
      // Check image display state after segment changes
      this.$nextTick(() => {
        if (this.shouldDisplayImage !== this.previousImageState) {
          this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
          this.isSegmentImageShowing = this.shouldDisplayImage;
          this.previousImageState = this.shouldDisplayImage;
        }

        // Select a new random placeholder when segment changes
        this.selectRandomPlaceholder();
      });
    }
  },
  mounted() {
    // Initial emission
    this.$nextTick(() => {
      this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
      this.isSegmentImageShowing = this.shouldDisplayImage;
      this.previousImageState = this.shouldDisplayImage;
    });
  },
  computed: {
    // Get the current random placeholder image
    randomPlaceholderImage() {
      return this.placeholderImages[this.currentPlaceholderImageIndex];
    },

    // Filter images from search results
    searchResultImages() {
      if (!this.searchResults || this.searchResults.length === 0) return [];

      // Look for images in search results by examining URLs
      return this.searchResults.filter(result => {
        // Skip if no URL
        if (!result.url) return false;

        // Skip Facebook and Instagram images as they block bot access
        const url = result.url.toLowerCase();
        if (url.includes('facebook.com') ||
            url.includes('fbcdn.net') ||
            url.includes('instagram.com')) {
          return false;
        }

        // Check for explicit image flag
        if (result.isImage) return true;

        // Common file extensions for images
        const imageExtensions = [
          '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
          '.ico', '.tiff', '.tif', '.img', '.heic', '.heif', '.raw', '.avif'
        ];

        // Check for file extensions
        const hasImageExtension = imageExtensions.some(ext => url.includes(ext));
        if (hasImageExtension) return true;

        // Check for common image patterns in URLs
        const imagePatterns = [
          'image', 'img', 'picture', 'photo', 'thumbnail', 'thumb',
          '/images/', '/pics/', '/photos/', '/avatars/', '/covers/',
          'media.licdn.com/dms/image', // LinkedIn images
          'profile-images', 'user-images',
          'fbcdn.net', // Facebook CDN
          'twimg.com', // Twitter images
          'instagram.com/p/', // Instagram posts
          'unsplash.com', // Unsplash
          'pexels.com', // Pexels
          'imgur.com', // Imgur
          'pinimg.com', // Pinterest
          'cloudfront.net', // AWS CloudFront (often used for images)
          'cloudinary.com', // Cloudinary image hosting
          'googleusercontent.com', // Google user content (often images)
          'gravatar.com' // Gravatars
        ];

        // Check for image service indicators in the URL
        return imagePatterns.some(pattern => url.includes(pattern));
      });
    },

    imageSegmentMapping() {
      const images = this.searchResultImages;
      if (!images || images.length === 0) return [];

      // Create a mapping for every segment
      const mapping = [];
      const totalSegments = this.totalSegments || 30;

      // Assign images to all segments, cycling through available images
      for (let segment = 0; segment < totalSegments; segment++) {
        const imageIndex = segment % images.length;
        mapping.push({ segmentIndex: segment, imageIndex });
      }

      return mapping;
    },

    // Get the current image based on active segment
    currentImage() {
      // Find the mapping entry for the current segment
      const mapping = this.imageSegmentMapping.find(m => m.segmentIndex === this.activeSegment);

      // If we have a mapping and enough images, return the mapped image
      if (mapping && this.searchResultImages.length > mapping.imageIndex) {
        return this.searchResultImages[mapping.imageIndex];
      }

      // Fallback to first image if available
      return this.searchResultImages.length > 0 ? this.searchResultImages[0] : null;
    },

    // Determine if we should display an image for current segment
    shouldDisplayImage() {
      // If documents panel is showing, don't show images. Citation panel OK
      if (this.showDocumentsPanel) return false;

      // If no images available, don't show panel
      if (!this.currentImage) return false;

      // Check if the current segment is in our mapping
      return this.imageSegmentMapping.some(mapping => mapping.segmentIndex === this.activeSegment);
    }
  },
  methods: {
    // Select a random placeholder image
    selectRandomPlaceholder() {
      this.currentPlaceholderImageIndex = Math.floor(Math.random() * this.placeholderImages.length);
    },

    // Add a new placeholder image to the collection
    addPlaceholderImage(imagePath) {
      if (!this.placeholderImages.includes(imagePath)) {
        this.placeholderImages.push(imagePath);
      }
    },

    // Public method to check if an image is shown for a specific segment
    isImageShownForSegment(segmentIndex) {
      if (this.showCitationPanel || this.showDocumentsPanel) return false;
      if (!this.searchResultImages.length) return false;

      return this.imageSegmentMapping.some(mapping => mapping.segmentIndex === segmentIndex);
    },

    formatUrl(url) {
      if (!url) return '';

      try {
        const urlObj = new URL(url);
        // Return domain name without www. prefix
        return urlObj.hostname.replace(/^www\./, '');
      } catch (e) {
        // If URL parsing fails, return the original truncated
        return url.length > 30 ? url.substring(0, 30) + '...' : url;
      }
    },

    handleImageError(event) {
      console.warn('Image load error:', event.target.alt);

      // Get the current image URL and add it to failed images
      const failedUrl = this.currentImage.url || this.currentImage.image;
      this.failedImageUrls.add(failedUrl);

      // Try to find another image for this segment that hasn't failed
      const availableImages = this.searchResultImages.filter(img =>
        !this.failedImageUrls.has(img.url || img.image)
      );

      if (availableImages.length > 0) {
        // Use the first available image that hasn't failed
        const newImage = availableImages[0];

        // Update the image source
        event.target.src = newImage.image || newImage.thumbnail;
        event.target.alt = newImage.title;

        // Update reference to the current image in your component's state
        // This depends on your data structure, might need adjustment
        this.$emit('image-changed', newImage);
      } else {
        // If all images have failed, show a random placeholder image
        const container = event.target.parentNode;

        // Select a random placeholder image
        this.selectRandomPlaceholder();
        const randomPlaceholder = this.randomPlaceholderImage;

        // Create a fallback image element
        const fallbackImg = document.createElement('img');
        fallbackImg.src = randomPlaceholder;
        fallbackImg.alt = "Fallback image";
        fallbackImg.className = "fallback-image";

        // Set inline style for height limitation
        fallbackImg.style.maxHeight = "500px";
        fallbackImg.style.width = "700px";
        fallbackImg.style.objectFit = "cover";

        // Hide the original image that failed to load
        event.target.style.display = 'none';

        // Add the fallback image to the container
        container.appendChild(fallbackImg);

        // Add error message below the image
        const errorMsg = document.createElement('div');
        errorMsg.className = 'image-error-message';
        errorMsg.textContent = 'Image could not be loaded, showing a stock image';
        container.appendChild(errorMsg);
      }
    },

    openImageUrl(url) {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  }
}
</script>

<style scoped>
/* Component styling */
.segment-image-panel {
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  padding: 1px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.panel-badge {
  display: flex;
  align-items: center;
  color: #3B82F6;
  font-weight: 500;
  font-size: 14px;
  gap: 6px;
}

.image-display {
  cursor: pointer;
  padding: 0 10% 0 10%;
}

.image-container {
  max-height: 550px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.05);
  position: relative;
}

.image-container img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.image-container img.fallback-image {
  object-fit: cover;
  opacity: 0.7;
}

.image-container:hover img {
  transform: scale(1.02);
}

.image-footer {
  margin-top: 12px;
}

.image-title {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 4px;
}

.image-source {
  font-size: 12px;
  color: #6B7280;
}

.image-source a {
  color: #3B82F6;
  text-decoration: none;
  transition: color 0.2s;
}

.image-source a:hover {
  color: #2563EB;
  text-decoration: underline;
}

.image-error-message {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px;
  color: white;
  font-size: 14px;
}

/* Empty state styling */
.empty-state-placeholder {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  padding: 0px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;
  animation: fadeSlideIn 0.3s ease-out;
}

.placeholder-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.placeholder-image-container .image-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}

.placeholder-image {
  width: 100%;
  max-width: 900px;
  height: auto;
  border-radius: 8px;
  opacity: 0.7;
  margin-bottom: 16px;
}

.placeholder-text {
  color: #6B7280;
  font-size: 14px;
  margin: 0;
}

/* Dark theme adjustments */
.theme--dark .segment-image-panel {
  background-color: #121212;
  border-left-color: #3B82F6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.theme--dark .empty-state-placeholder {
  background-color: rgba(0, 0, 0, 0.1);
  border-left-color: #6B7280;
}

.theme--dark .image-container {
  background: rgba(0, 0, 0, 0.2);
}

.theme--dark .image-source {
  color: #9CA3AF;
}

.theme--dark .image-source a {
  color: #60A5FA;
}

.theme--dark .image-source a:hover {
  color: #93C5FD;
}

.theme--dark .placeholder-text {
  color: #9CA3AF;
}

/* Animation for panel appearance */
@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.segment-image-panel, .empty-state-placeholder {
  animation: fadeSlideIn 0.3s ease-out;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .segment-image-panel, .empty-state-placeholder {
    padding: 12px;
    margin-bottom: 12px;
  }

  .image-display {
    padding: 0;
  }

  .image-container {
    height: 250px;
  }

  .placeholder-image {
    max-width: 260px;
  }
}
</style>
